/** @jsx jsx */
import React from 'react';

import { jsx } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Heading,
  HeadingProps,
  InputProps,
  Link,
  Text,
  TextProps,
} from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { InputType } from '@opendoor/bricks/system/input.types';
import { useTheme } from '@opendoor/bricks/system/theme';
import { space } from '@opendoor/bricks/theme/eero';

export const INVALID_EMAIL_ERROR = "Looks like this isn't a valid email.";

export const AccountFormWrapper: React.FC<BoxProps> = (props) => {
  return (
    <Box
      width="100%"
      maxWidth="500px"
      padding={[7, 7, 0]}
      textAlign={['left', 'left', 'center']}
      {...props}
    />
  );
};

export type DisclaimerProps = {
  location?: 'top' | 'bottom';
  formatText?: (formatProps: { actionText: string }) => string;
};

export const PHONE_DEFAULT_DISCLAIMER = (actionText: string) => `
By clicking "${actionText}" I consent to receive marketing calls and texts from Opendoor
and its affiliates to the number provided, including by automated means. I may opt out
at any time and consent is not a condition of purchase.
`;

export const AccountFormHeader: React.FC<Omit<HeadingProps, 'size' | 'as'>> = (props) => {
  // eslint-disable-next-line jsx-a11y/heading-has-content
  return <Heading size="UNSAFE_h2" as="h2" mb={7} fontSize={['s3', 'UNSAFE_h2']} {...props} />;
};

export const FormWrapper: React.FC<FlexProps> = (props) => {
  return <Flex width="100%" flexDirection="column" {...props} />;
};

export const BodyWrapper: React.FC<TextProps> = (props) => {
  return <Text my={2} {...props} />;
};

export const CtaButtonWrapper: React.FC<BoxProps> = (props) => {
  return <Box mt={9} mb={5} {...props} />;
};

export const Disclaimer: React.FC<BoxProps & { desktopNoWrap?: boolean }> = ({
  children,
  desktopNoWrap = false,
  ...props
}) => {
  const { name } = useTheme();
  const { isMobile } = useDevice({ reevaluateAfterMount: true });
  const whiteSpaceCSS = desktopNoWrap && !isMobile ? 'nowrap' : 'normal';
  if (name == 'novo') {
    return (
      <Text size={desktopNoWrap ? '80' : '90'} color="warmgrey900" whiteSpace={whiteSpaceCSS}>
        {children}
      </Text>
    );
  }

  return (
    <Box mt={desktopNoWrap ? null : 9} mb={desktopNoWrap ? 9 : null} {...props}>
      <Text
        size="80"
        color="neutrals90"
        whiteSpace={whiteSpaceCSS}
        textAlign={desktopNoWrap ? 'left' : 'inherit'}
      >
        {children}
      </Text>
    </Box>
  );
};

export const EmailDisclaimerText: React.FC<{ analyticsName: string }> = ({ analyticsName }) => {
  const { name } = useTheme();
  const linkTextStyling = {
    color: name === 'novo' ? 'warmgrey900' : 'neutrals90',
    textDecoration: 'underline',
  };
  return (
    <Disclaimer desktopNoWrap={true}>
      By clicking “Continue,” you agree to Opendoor's and its affiliates’&nbsp;
      <Link
        href="https://www.opendoor.com/terms"
        display="inline"
        target="_blank"
        analyticsName={`${analyticsName}-terms`}
        aria-label="Opens a new page with Opendoor's terms of service"
        __css={linkTextStyling}
      >
        Terms of Service
      </Link>
      &nbsp;and&nbsp;
      <Link
        href="https://www.opendoor.com/privacy"
        display="inline"
        target="_blank"
        analyticsName={`${analyticsName}-privacy-policy`}
        aria-label="Opens a new page with Opendoor's privacy policy"
        __css={linkTextStyling}
      >
        Privacy Policy
      </Link>
      .
    </Disclaimer>
  );
};

interface IBasicTextInput
  extends Pick<
    InputProps,
    | 'name'
    | 'autoFocus'
    | 'className'
    | 'disabled'
    | 'onBlur'
    | 'onKeyDown'
    | 'onFocus'
    | 'required'
    | 'role'
    | 'autoComplete'
  > {
  /**
   * Callback handler when the current value of the input has changed
   */
  onChange: (name: string, value?: string | number, validFormat?: boolean) => void;
  // Optional:

  id?: string;
  /**
   * Accessibility label to use, in scenarios where the input has no visible label. A11y: It's usefult for screen readers
   */
  ariaLabel?: string;

  /**
   * Accessibility label to indicate the item it is labelled by. A11y: It's useful for screen readers
   */
  ariaLabelledby?: string;

  /**
   * Input corner style variations
   */
  designStyle?: 'rounded' | 'squared' | 'rounded-square';

  /**
   * Text alignment of input value
   */
  alignText?: 'left' | 'center' | 'right';

  /**
   * Whether or not the input should assert null values
   */
  assertNotNull?: boolean;

  /**
   * Current value of the input
   */
  content?: string | number;

  /**
   * Message displayed when the input has an error
   */
  errorMessage?: string;

  /**
   * Whether or not the input has an error
   */
  hasError?: boolean;

  /**
   * Sets the input's `name` attribute used to identify the input when submitting the form (Duplicate of name prop)
   */
  inputName?: string;

  /**
   * Input label size
   */
  inputLabelSize?: 'small' | 'medium' | 'large';

  /**
   * Contains a hint to the user of what can be entered in the input
   */
  placeholder?: string;

  /**
   * Label title
   */
  title?: string;

  /**
   * Label subtitle
   */
  subtitle?: string;

  /**
   * Sets the input width to 100% when true
   */
  unboundedWidth?: boolean;

  /**
   * Adjusts styling when the input has a button appended to it
   */
  isCtaInput?: boolean;
}

interface UNSAFE_ISingleLineInputWrapperProps extends IBasicTextInput {
  inputType?: InputType;
  onChange: (name: string, value?: string) => void;
}

// eslint-disable-next-line no-restricted-syntax
const UNSAFE_SingleLineInputWrapper = styled.div<
  Pick<UNSAFE_ISingleLineInputWrapperProps, 'unboundedWidth'>
>`
  width: ${(props) => (props.unboundedWidth ? '100%' : '350px')};
  @media (max-width: 768px) {
    width: 100%;
  }
`;

// eslint-disable-next-line no-restricted-syntax
export const Form = styled.form`
  width: 100%;
  margin-top: ${space[7]};

  ${UNSAFE_SingleLineInputWrapper} {
    margin: ${space[5]}px 0;
  }
`;
